/**
 * L'onglet Contrôles de la fiche d'un élève : la route `school-assessments` en contexte tuteur
 * (`SchoolAssessmentsScreen.js`, `isTutorContext`, que la carte du prochain contrôle des activités
 * de l'élève ouvrait avec `{ studentId, student }`).
 *
 * Les gestes sont ceux de « Mes contrôles » de l'élève, par ses crochets (le même formulaire, les mêmes
 * écritures, la même confirmation de suppression), pour l'élève que le tuteur regarde — la table
 * `TUTOR_STUDENT_ASSESSMENTS_EQUIVALENCE` :
 * - la lecture : `getSchoolAssessments { student_id, upcoming: true }` à chaque ouverture de l'onglet
 *   (l'ancien écran était poussé à chaque fois) et à chaque relecture ;
 * - « Ajouter un contrôle » / « Modifier » : `createSchoolAssessment` / `updateSchoolAssessment { id }`
 *   avec `student_id` de l'élève (`AddSchoolAssessmentModal`, `studentId` et `user={targetStudent}` : la
 *   classe et les objectifs de l'élève) ;
 * - « Supprimer » : « Supprimer ce contrôle ? », puis `deleteSchoolAssessment { id }`.
 *
 * Chaque écriture est mesurée par `useApiMutation` (`gesture.*`, sous le nom de son service, l'espace
 * tuteur lu au moment du geste) — comme chez l'élève.
 *
 * Sans en-tête (`hideHeader`, sous celui de la fiche) : ni « Contrôles de {prénom} », ni la croix.
 */
import { useCallback, useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { schoolAssessmentsService } from "../../../../../../data/services/schoolAssessmentsService"
import { type TutorStudentProfilePageProps } from "../../../../../../design-system/pages/TutorStudentProfilePage"
import { makeTutorStudentAssessmentsViewModel } from "../../../../view-models/tutor/Students/tutorStudentAssessmentsViewModel"
import { useStudentAssessmentForm } from "../../student/Assessments/useStudentAssessmentForm"
import { useStudentAssessmentsState } from "../../student/Assessments/useStudentAssessmentsState"
import { type TutorStudentTab, type TutorStudentTabArgs } from "./tutorStudentTabs"

export type TutorStudentAssessmentsTabProps = NonNullable<TutorStudentProfilePageProps["assessments"]>

/** Ce que l'ancien code envoyait, geste par geste : chaque ligne est jouée par un test sur le crochet. */
export const TUTOR_STUDENT_ASSESSMENTS_EQUIVALENCE = {
    open_tab: {
        gesture: "Ouvrir l'onglet Contrôles (l'ancienne carte du prochain contrôle → `school-assessments`)",
        legacy: "student/schoolAssessments/SchoolAssessmentsScreen.js:66-82",
        calls: [{ service: "getSchoolAssessments", urlParams: "{ student_id: élève, upcoming: true }" }],
    },
    refresh: {
        gesture: "Relire la liste (tirer pour rafraîchir, « Réessayer »)",
        legacy: "SchoolAssessmentsScreen.js:93-96",
        calls: [{ service: "getSchoolAssessments", urlParams: "{ student_id: élève, upcoming: true }" }],
    },
    delete: {
        gesture: "« Supprimer » puis « Supprimer » dans « Supprimer ce contrôle ? »",
        legacy: "SchoolAssessmentsScreen.js:108-122",
        calls: [{ service: "deleteSchoolAssessment", routeParams: "{ id: contrôle }" }],
    },
    cancel_delete: {
        gesture: "« Supprimer » puis « Annuler » : rien ne part",
        legacy: "SchoolAssessmentsScreen.js:108-122",
        calls: [],
    },
    create: {
        gesture: "« Ajouter un contrôle », remplir, enregistrer : pour l'élève regardé",
        legacy: "student/schoolAssessments/AddSchoolAssessmentModal.js:104-125",
        calls: [
            {
                service: "createSchoolAssessment",
                data: "{ student_id: élève, school_subject_id, date, chapter_ids, custom_chapters?, key_requirements? }",
            },
        ],
    },
} as const

export function useTutorStudentAssessmentsTab(
    args: TutorStudentTabArgs
): TutorStudentTab<TutorStudentAssessmentsTabProps> {
    const { studentId, visible, active } = args
    const dispatch = useDispatch()
    const reading = visible && active && studentId != null

    const viewModel = useMemo(makeTutorStudentAssessmentsViewModel, [])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const vm = useSelector((state: any) => viewModel(state, studentId))

    const refresh = useCallback(() => {
        if (studentId == null) return
        dispatch(
            apiRequest(
                schoolAssessmentsService.getSchoolAssessments,
                {},
                {},
                { student_id: studentId, upcoming: true }
            ) as never
        )
    }, [dispatch, studentId])

    // Une lecture par ouverture de l'onglet, et par élève : l'écran d'avant se relisait à chaque fois qu'on le
    // poussait. La liste déjà rangée reste affichée pendant la relecture.
    useEffect(() => {
        if (reading) refresh()
    }, [reading, refresh])

    const { assessmentForm, openAdd, openEdit } = useStudentAssessmentForm({
        studentId: vm.studentId,
        studentClass: vm.studentClass,
        subjects: vm.subjects,
        edits: vm.edits,
    })
    // La croix de la page n'existe pas sous l'en-tête de la fiche : `onClose` n'est pas relayé.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { onClose: _onClose, ...state } = useStudentAssessmentsState({ onEdit: openEdit })

    if (studentId == null) return { props: undefined, overlay: null }

    return {
        props: {
            assessments: vm.assessments,
            loading: vm.loading,
            refreshing: vm.refreshing,
            error: vm.error,
            onRetry: refresh,
            onRefresh: refresh,
            onAddAssessment: openAdd,
            assessmentForm,
            emptyMessage: vm.emptyMessage,
            ...state,
        },
        overlay: null,
    }
}
